import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatearFecha, formatearFechaHora, type EstadoOT, type Pagina, type SujetoEvaluable } from '@cmms/shared';
import { api, consulta } from '../servicios/api.js';
import { useSesion } from '../servicios/sesion.js';
import { Aviso, Cargando, Etiqueta, EstadoOtEtiqueta, GaleriaImagenes, Vacio, type AdjuntoImagen } from '../componentes/piezas.js';
import { BotonVolver } from '../componentes/boton-volver.js';
import { EnlaceRuta } from '../componentes/enlace-ruta.js';
import { ModalDejarObservacion } from './ordenes.js';

interface ObservacionDePersona {
  id: string;
  texto: string;
  fechaVencimiento: string | null;
  vencida: boolean;
  resuelta: boolean;
  ordenTrabajoNumero: number | null;
  ordenTrabajoGeneradaId: string | null;
  ordenTrabajoGeneradaNumero: number | null;
  creadoPor: string | null;
  creadoEl: string;
  adjuntos: AdjuntoImagen[];
}

interface OtDePersona {
  id: string;
  numero: number;
  titulo: string;
  estado: EstadoOT;
  creadoEl: string;
}

/**
 * Ficha de una persona (§12.7): sus observaciones pendientes y todas sus OT,
 * igual que el detalle de un activo. Desde aquí se deja una observación nueva
 * y se crea la OT de seguimiento de una pendiente.
 */
export function DetallePersona(): JSX.Element {
  const { id = '' } = useParams();
  const cliente = useQueryClient();
  const { puede } = useSesion();
  const [observacionAbierta, setObservacionAbierta] = useState(false);

  const { data: persona, isLoading, isError } = useQuery({
    queryKey: ['persona', id],
    queryFn: () => api.get<SujetoEvaluable>(`/sujetos-evaluables/${id}`),
    enabled: id !== '',
  });

  const { data: observaciones } = useQuery({
    queryKey: ['observaciones-sujeto', id],
    queryFn: () => api.get<ObservacionDePersona[]>(`/sujetos-evaluables/${id}/observaciones`),
    enabled: id !== '',
  });

  const { data: ordenes } = useQuery({
    queryKey: ['ordenes-trabajo', 'persona', id],
    queryFn: () =>
      api.get<Pagina<OtDePersona>>(
        `/ordenes-trabajo${consulta({ sujetoEvaluableId: id, tamano: 50, orden: 'numero', direccion: 'desc' })}`,
      ),
    enabled: id !== '',
  });

  // Lo de la ficha también aparece en la cola de Observaciones y en los contadores.
  const refrescarObservaciones = async (): Promise<void> => {
    await Promise.all([
      cliente.invalidateQueries({ queryKey: ['observaciones-sujeto', id] }),
      cliente.invalidateQueries({ queryKey: ['observaciones-sujeto-pendientes'] }),
    ]);
  };

  const dejarObservacion = useMutation({
    mutationFn: (observacion: { texto: string; fechaVencimiento: string | null; adjuntos: string[] }) =>
      api.post(`/sujetos-evaluables/${id}/observaciones`, observacion),
    onSuccess: async () => {
      setObservacionAbierta(false);
      await refrescarObservaciones();
    },
  });

  const marcarAtendida = useMutation({
    mutationFn: (observacionId: string) =>
      api.post(`/sujetos-evaluables/${id}/observaciones/${observacionId}/resolver`, { resuelta: true }),
    onSuccess: refrescarObservaciones,
  });

  if (isLoading) return <Cargando />;
  if (isError || !persona) {
    return (
      <>
        <BotonVolver a="/personal" etiqueta="Volver a Personal" />
        <Aviso tono="error">No se encontró la persona.</Aviso>
      </>
    );
  }

  const pendientes = (observaciones ?? []).filter((o) => !o.resuelta);
  const atendidas = (observaciones ?? []).filter((o) => o.resuelta);

  return (
    <>
      <BotonVolver a="/personal" etiqueta="Volver a Personal" />

      <div className="cabecera-pagina">
        <div>
          <div className="numero-registro">PERSONA</div>
          <h1>{persona.nombreCompleto}</h1>
          <p style={{ margin: 0, color: 'var(--texto-medio)' }}>
            {persona.rol}
            {persona.ubicacion ? ` · ${persona.ubicacion}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Etiqueta tono={persona.activo ? 'verde' : 'gris'}>{persona.activo ? 'Activa' : 'Inactiva'}</Etiqueta>
          {puede('ot:cambiar_estado') ? (
            <button type="button" className="boton secundario pequeno" onClick={() => setObservacionAbierta(true)}>
              Dejar observación
            </button>
          ) : null}
        </div>
      </div>

      <section className="panel">
        <div className="panel-cabecera">
          <h2>Observaciones pendientes</h2>
        </div>
        <div className="panel-cuerpo">
          {pendientes.length === 0 ? (
            <Vacio titulo="Sin observaciones pendientes" />
          ) : (
            <div className="hilo">
              {pendientes.map((obs) => (
                <article key={obs.id} className="hilo-evento">
                  <div className="hilo-marca" aria-hidden="true">
                    !
                  </div>
                  <div className="hilo-cuerpo">
                    <div className="hilo-meta">
                      {obs.creadoPor ?? 'Sistema'} · {formatearFechaHora(obs.creadoEl)}
                      {obs.ordenTrabajoNumero ? ` · OT #${obs.ordenTrabajoNumero}` : ''}
                      {obs.fechaVencimiento ? (
                        <span style={obs.vencida ? { color: 'var(--falla)' } : undefined}>
                          {' · '}
                          {obs.vencida ? 'venció el' : 'atender antes del'} {formatearFecha(obs.fechaVencimiento)}
                        </span>
                      ) : null}
                    </div>
                    <div className="hilo-texto">{obs.texto}</div>
                    {obs.adjuntos.length > 0 ? (
                      <div style={{ marginTop: 8 }}>
                        <GaleriaImagenes fotos={obs.adjuntos} titulo={(f) => `Observación · ${f.nombreArchivo}`} />
                      </div>
                    ) : null}
                    <div className="acciones-fila" style={{ marginTop: 8 }}>
                      {puede('ot:crear') && !obs.ordenTrabajoGeneradaId ? (
                        <EnlaceRuta
                          to={`/ordenes/nueva?sujetoEvaluableId=${id}&observacionId=${obs.id}&titulo=${encodeURIComponent(
                            `Seguimiento: ${obs.texto.slice(0, 80)}`,
                          )}`}
                          className="boton secundario pequeno"
                        >
                          Crear OT
                        </EnlaceRuta>
                      ) : null}
                      {obs.ordenTrabajoGeneradaId ? (
                        <EnlaceRuta to={`/ordenes/${obs.ordenTrabajoGeneradaId}`}>
                          OT de seguimiento #{obs.ordenTrabajoGeneradaNumero}
                        </EnlaceRuta>
                      ) : null}
                      {puede('sujeto_evaluable:escribir') ? (
                        <button
                          type="button"
                          className="boton secundario pequeno"
                          disabled={marcarAtendida.isPending}
                          onClick={() => marcarAtendida.mutate(obs.id)}
                        >
                          Marcar atendida
                        </button>
                      ) : null}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {atendidas.length > 0 ? (
        <section className="panel">
          <div className="panel-cabecera">
            <h2>Observaciones atendidas</h2>
          </div>
          <div className="panel-cuerpo">
            <div className="hilo">
              {atendidas.map((obs) => (
                <article key={obs.id} className="hilo-evento">
                  <div className="hilo-marca" aria-hidden="true">
                    ✓
                  </div>
                  <div className="hilo-cuerpo">
                    <div className="hilo-meta">
                      {obs.creadoPor ?? 'Sistema'} · {formatearFechaHora(obs.creadoEl)}
                      {obs.ordenTrabajoNumero ? ` · OT #${obs.ordenTrabajoNumero}` : ''}
                    </div>
                    <div className="hilo-texto">{obs.texto}</div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="panel">
        <div className="panel-cabecera">
          <h2>Órdenes de trabajo</h2>
        </div>
        <div className="panel-cuerpo">
          {!ordenes || ordenes.datos.length === 0 ? (
            <Vacio titulo="Esta persona no tiene órdenes de trabajo" />
          ) : (
            <div className="tabla-contenedor">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Título</th>
                    <th>Estado</th>
                    <th>Creada</th>
                  </tr>
                </thead>
                <tbody>
                  {ordenes.datos.map((o) => (
                    <tr key={o.id}>
                      <td>
                        <EnlaceRuta to={`/ordenes/${o.id}`}>#{o.numero}</EnlaceRuta>
                      </td>
                      <td>{o.titulo}</td>
                      <td>
                        <EstadoOtEtiqueta estado={o.estado} />
                      </td>
                      <td>{formatearFecha(o.creadoEl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {observacionAbierta ? (
        <ModalDejarObservacion
          entidadNombre={persona.nombreCompleto}
          cargando={dejarObservacion.isPending}
          alCerrar={() => setObservacionAbierta(false)}
          alGuardar={(observacion) => dejarObservacion.mutate(observacion)}
        />
      ) : null}
    </>
  );
}
